"use client";
import useSWR from 'swr';
import { TokenTable } from './TokensTable';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import { TableSkeleton } from './TableSkeleton';
import type { Token } from '@/utils/types/token';

interface TokensTableSWRProps {
  initialTokens: Token[];
  isStocks?: boolean;
}

export function TokensTableSWRInner({ initialTokens, isStocks = false }: TokensTableSWRProps) {
  const searchParams = useSearchParams();
  let page: number | undefined = undefined;
  if (searchParams) {
    const pageParam = searchParams.get('page');
    if (pageParam && /^\d+$/.test(pageParam)) {
      page = parseInt(pageParam, 10);
      if (isNaN(page) || page < 1) page = undefined;
    }
  }

  const endpoint = isStocks
    ? '/tkn_lnd_pg_api/tokens-stocks/?sortParam=mc&sortDir=desc&take=200'
    : '/tkn_lnd_pg_api/tokens/?sortParam=mc&sortDir=desc&take=200';

  const { data, error } = useSWR(
    endpoint,
    (url: string) => fetch(url).then(res => res.json()),
    {
      fallbackData: initialTokens,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 30000,
      focusThrottleInterval: 5000,
      errorRetryCount: 2,
      errorRetryInterval: 1000,
    }
  );

  const tokens: Token[] = Array.isArray(data) ? data : data?.tokens || [];

  if (error) {
    return <TableSkeleton />;
  }

  return <TokenTable tokens={tokens} page={page} isStocks={isStocks} />;
}

export function TokensTableSWR({ initialTokens, isStocks = false }: TokensTableSWRProps) {
  return (
    <Suspense fallback={<TableSkeleton />}>
      <TokensTableSWRInner initialTokens={initialTokens} isStocks={isStocks} />
    </Suspense>
  );
}

export default TokensTableSWR;
